import { cn } from '@/lib/utils';

interface Props {
  data: number[];
  /** SVG width. Height computed from aspectRatio. */
  width?: number;
  height?: number;
  /** Stroke color. Defaults to current text color (use Tailwind text-* on parent). */
  className?: string;
  /** Fill the area under the line at low opacity. */
  area?: boolean;
}

/**
 * Tiny inline sparkline. SVG path drawn through normalized data points.
 * No axes, no labels — purely a trend hint for KPI cards. Uses currentColor
 * so the parent's Tailwind text-* drives the stroke.
 */
export function Sparkline({ data, width = 80, height = 24, className, area }: Props) {
  if (data.length === 0) return null;

  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return [x, y] as const;
  });

  const linePath = points.reduce(
    (acc, [x, y], i) => acc + (i === 0 ? `M ${x},${y}` : ` L ${x},${y}`),
    '',
  );

  const areaPath = area
    ? `${linePath} L ${width},${height} L 0,${height} Z`
    : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {areaPath && (
        <path d={areaPath} fill="currentColor" fillOpacity={0.12} />
      )}
      <path
        d={linePath}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
